/** Discounts list, type picker, editor and its running summary. Conventions: see `common.ts`. */
const discounts: Record<string, string> = {
  'types.PRODUCT': 'Amount off products',
  'types.BXGY': 'Buy X get Y',
  'types.ORDER': 'Amount off order',
  'types.SHIPPING': 'Free shipping',

  'type_descriptions.PRODUCT': 'Discount specific products or collections of products',
  'type_descriptions.BXGY': 'Discount specific products or collections of products',
  'type_descriptions.ORDER': 'Discount the total order amount',
  'type_descriptions.SHIPPING': 'Offer free shipping on an order',

  'classes.product': 'Product discounts',
  'classes.order': 'Order discounts',
  'classes.shipping': 'Shipping discounts',
  'class_short.product': 'Product',
  'class_short.order': 'Order',
  'class_short.shipping': 'Shipping',
  'class_noun.product': 'Product discount',
  'class_noun.order': 'Order discount',
  'class_noun.shipping': 'Shipping discount',

  'channels.ONLINE_STORE': 'Online Store',

  'status.ACTIVE': 'Active',
  'status.SCHEDULED': 'Scheduled',
  'status.EXPIRED': 'Expired',
  'status.DISABLED': 'Disabled',

  'methods.CODE': 'Discount code',
  'methods.AUTOMATIC': 'Automatic discount',

  'sort.newest': 'Newest first',
  'sort.oldest': 'Oldest first',
  'sort.title_asc': 'Name A–Z',
  'sort.title_desc': 'Name Z–A',
  'sort.used_desc': 'Most used',
  'sort.ends_soon': 'Ending soonest',

  'value.percent_off': '{value}% off',
  'value.amount_off': '{amount} off',
  'value.free': 'free',

  'list.title': 'Discounts',
  'list.in_view': '{total} in this view',
  'list.loading': 'Loading your discounts…',
  'list.create': 'Create discount',
  'list.views': 'Discount views',
  'list.activate': 'Activate',
  'list.deactivate': 'Deactivate',
  'list.empty_filtered_title': 'No discounts match these filters',
  'list.empty_title': 'No discounts yet',
  'list.empty_filtered_description': 'Try a different search, or clear the filters to see everything.',
  'list.empty_description': 'Run a festival offer, reward a bulk order, or win back lapsed customers.',
  'list.create_first': 'Create your first discount',
  'list.select_all': 'Select all discounts on this page',
  'list.select_one': 'Select {title}',
  'list.actions_for': 'Actions for {title}',
  'list.automatic': 'Automatic',
  'list.ends': 'Ends {date}',
  'list.errors.update_failed': 'Could not update this discount.',
  'list.columns.discount': 'Discount',
  'list.columns.status': 'Status',
  'list.columns.method': 'Method',
  'list.columns.type': 'Type',
  'list.columns.combines_with': 'Combines with',
  'list.columns.used': 'Used',
  'list.usage': '{used} used',
  'list.usage_of_limit': '{used} of {limit} used',
  'list.describe.order': '{off} the order',
  'list.describe.all_products': '{off} all products',
  'list.describe.products': '{off} {number} products',
  'list.describe.collections': '{off} {number} collections',
  'list.describe.spend': 'Spend {amount}',
  'list.describe.buy': 'Buy {quantity}',
  'list.describe.bxgy': '{buy}, get {quantity} {reward}',
  'list.describe.shipping_countries': 'Free shipping to {number} countries',
  'list.describe.shipping': 'Free shipping',

  'method.CODE': 'Discount code',
  'method.AUTOMATIC': 'Automatic',

  'filters.starts': 'Starts',
  'filters.ends': 'Ends',
  'filters.redemptions': 'Times redeemed',
  'filters.performance': 'Performance',
  'filters.has_redemptions': 'Redeemed at least once',
  'filters.never_redeemed': 'Never redeemed',

  'kpi.redemptions': 'Redemptions',
  'kpi.redemptions_hint': 'Across the discounts shown',
  'kpi.best': 'Busiest discount',
  'kpi.best_hint': 'Times the top performer was used',
  'kpi.never_used': 'Never redeemed',
  'kpi.never_used_hint': 'Worth rewording or retiring',

  'list.columns.starts': 'Starts',
  'list.columns.ends': 'Ends',

  'bulk.deactivate_title': 'Deactivate discounts',
  'bulk.deactivate_message_one':
    'Deactivate {count} discount? Shoppers will stop being able to use it straight away.',
  'bulk.deactivate_message_other':
    'Deactivate {count} discounts? Shoppers will stop being able to use them straight away.',

  'toast.activated': 'Discount activated.',
  'toast.deactivated': 'Discount deactivated.',
  'toast.bulk_activated_one': '{count} discount activated.',
  'toast.bulk_activated_other': '{count} discounts activated.',
  'toast.bulk_deactivated_one': '{count} discount deactivated.',
  'toast.bulk_deactivated_other': '{count} discounts deactivated.',
  'toast.bulk_partial': '{done} updated, {failed} could not be.',

  'toolbar.search_label': 'Search discounts',
  'toolbar.search_placeholder': 'Search by name or code',

  'type_dialog.title': 'Select discount type',

  'form.back': 'Back to discounts',
  'form.edit_title': 'Edit discount',
  'form.create_title': 'Create discount',
  'form.no_permission': 'You do not have permission to manage discounts.',
  'form.load_failed': 'This discount could not be loaded.',
  'form.errors.save_failed': 'Could not save this discount.',
  'form.errors.delete_failed': 'Could not delete this discount.',

  'validation.title': 'Give the discount a name.',
  'validation.code': 'Enter a discount code of at least three characters.',
  'validation.value': 'Enter a discount value.',
  'validation.percent_max': 'A percentage cannot exceed 100.',
  'validation.applies_to': 'Choose what this discount applies to.',
  'validation.buy_quantity': 'Set how many items the customer must buy.',
  'validation.buy_amount': 'Set how much the customer must spend.',
  'validation.buy_items': 'Choose the items the customer buys.',
  'validation.get_quantity': 'Set how many items the customer gets.',
  'validation.get_items': 'Choose the items the customer gets.',
  'validation.get_value': 'Set the discounted value the customer gets.',
  'validation.countries': 'Choose at least one country.',
  'validation.customers': 'Choose at least one customer.',
  'validation.min_amount': 'Enter the minimum purchase amount.',
  'validation.min_quantity': 'Enter the minimum quantity of items.',
  'validation.usage_limit': 'Enter a usage limit.',
  'validation.end_date': 'Enter an end date.',
  'validation.end_in_past': 'The end date has already passed. Pick a date in the future.',
  'validation.end_after_start': 'The end date must be after the start date.',

  'method_card.method': 'Method',
  'method_card.code': 'Discount code',
  'method_card.generate': 'Generate random code',
  'method_card.code_hint': 'Customers must enter this code at checkout.',
  'method_card.automatic_hint': 'Customers will see this discount applied at checkout without entering a code.',
  'method_card.title': 'Title',
  'method_card.title_placeholder': 'Diwali weekend sale',
  'method_card.title_hint': 'Only you and your staff see this - it names the discount in your list.',

  'value_card.title': 'Discount value',
  'value_card.type': 'Type',
  'value_card.percentage': 'Percentage',
  'value_card.fixed': 'Fixed amount',
  'value_card.percentage_off': 'Percentage off',
  'value_card.amount_off': 'Amount off',
  'value_card.fixed_hint': 'The amount comes off the eligible items as a whole, not off each one.',
  'value_card.applies_to': 'Applies to',

  'buys.title': 'Customer buys',
  'buys.requirement': 'Purchase requirement',
  'buys.min_quantity': 'Minimum quantity of items',
  'buys.min_amount': 'Minimum purchase amount',
  'buys.any_items_from': 'Any items from',

  'gets.title': 'Customer gets',
  'gets.description': 'Customers must add the quantity of items specified below to their cart.',
  'gets.any_items_from': 'Any items from',
  'gets.discounted_value': 'At a discounted value',
  'gets.percentage': 'Percentage',
  'gets.percentage_off': 'Percentage off',
  'gets.amount_off_each': 'Amount off each',
  'gets.free': 'Free',
  'gets.cap_uses': 'Set a maximum number of uses per order',
  'gets.max_uses': 'Maximum uses',

  'fields.quantity': 'Quantity',
  'fields.amount': 'Amount',
  'fields.all_products': 'All products',
  'fields.specific_products': 'Specific products',
  'fields.specific_collections': 'Specific collections',
  'fields.search_products': 'Search products',
  'fields.search_collections': 'Search collections',
  'fields.add_tags': 'Add tags',
  'fields.add_tag': 'Add tag',
  'fields.remove_item': 'Remove {label}',

  'countries_card.title': 'Countries',
  'countries_card.legend': 'Countries this applies to',
  'countries_card.all': 'All countries',
  'countries_card.selected': 'Selected countries',
  'countries_card.shipping_rates': 'Shipping rates',
  'countries_card.exclude_rates': 'Exclude shipping rates over a certain amount',

  'countries.IN': 'India',
  'countries.AE': 'United Arab Emirates',
  'countries.GB': 'United Kingdom',
  'countries.US': 'United States',
  'countries.CA': 'Canada',
  'countries.AU': 'Australia',
  'countries.SG': 'Singapore',
  'countries.MY': 'Malaysia',
  'countries.NZ': 'New Zealand',
  'countries.LK': 'Sri Lanka',
  'countries.NP': 'Nepal',
  'countries.BD': 'Bangladesh',

  'eligibility.title': 'Eligibility',
  'eligibility.legend': 'Who can use this discount',
  'eligibility.all': 'All customers',
  'eligibility.specific': 'Specific customers',
  'eligibility.search': 'Search customers',
  'eligibility.hint':
    'Checked at checkout, where the shopper identifies themselves - a cart may show the discount before it is confirmed they qualify.',

  'minimum.title': 'Minimum purchase requirements',
  'minimum.legend': 'Minimum purchase requirement',
  'minimum.none': 'No minimum requirements',
  'minimum.amount': 'Minimum purchase amount (₹)',
  'minimum.quantity': 'Minimum quantity of items',

  'max_uses.title': 'Maximum discount uses',
  'max_uses.cap_total': 'Limit number of times this discount can be used in total',
  'max_uses.total_uses': 'Total uses',
  'max_uses.once_per_customer': 'Limit to one use per customer',

  'combinations.title': 'Combinations',
  'combinations.none':
    "This discount won't combine with other product, order, or shipping discounts in the customer's cart.",
  'combinations.some': 'Both discounts must allow the combination for them to apply together.',

  'dates.title': 'Active dates',
  'dates.start_date': 'Start date',
  'dates.start_time': 'Start time ({zone})',
  'dates.set_end': 'Set end date',
  'dates.end_date': 'End date',
  'dates.end_time': 'End time ({zone})',

  'channels_card.title': 'Sales channel access',
  'channels_card.feature': 'Allow discount to be featured on selected channels',

  'tags.title': 'Tags',

  'picker.products.title': 'Add products',
  'picker.products.placeholder': 'Search products',
  'picker.products.empty': 'No products match that search.',
  'picker.collections.title': 'Add collections',
  'picker.collections.placeholder': 'Search collections',
  'picker.collections.empty': 'No collections match that search.',
  'picker.customers.title': 'Add customers',
  'picker.customers.placeholder': 'Search by name, email or phone',
  'picker.customers.empty': 'No customers match that search.',
  'picker.browse': 'Browse',

  'summary.no_code': 'No discount code yet',
  'summary.untitled': 'Untitled discount',
  'summary.code': 'Code',
  'summary.automatic': 'Automatic discount',
  'summary.type': 'Type',
  'summary.details': 'Details',
  'summary.tags': 'Tags',

  'summary.selection.all_products': 'all products',
  'summary.selection.selected_products': 'selected products',
  'summary.selection.selected_collections': 'selected collections',
  'summary.selection.products_one': '{count} product',
  'summary.selection.products_other': '{count} products',
  'summary.selection.collections_one': '{count} collection',
  'summary.selection.collections_other': '{count} collections',

  'summary.headline.no_value': 'a discount',
  'summary.headline.order': '{value} the order total',
  'summary.headline.product': '{value} {selection}',
  'summary.headline.shipping_all': 'Free shipping to all countries',
  'summary.headline.shipping_selected': 'Free shipping to selected countries',
  'summary.headline.shipping_countries': 'Free shipping to {number} countries',
  'summary.headline.buy_quantity': 'Buy {quantity} of {selection}',
  'summary.headline.buy_amount': 'Spend {amount} on {selection}',
  'summary.headline.bxgy': '{buy}, get {quantity} of {selection} {reward}',

  'summary.details.customers_all': 'All customers',
  'summary.details.customers_selected': 'Selected customers',
  'summary.details.customers_count': '{number} customers',
  'summary.details.channels_one': 'For {count} sales channel',
  'summary.details.channels_other': 'For {count} sales channels',
  'summary.details.online_store': 'For Online Store',
  'summary.details.countries_all': 'For all countries',
  'summary.details.countries_count': 'For {number} countries',
  'summary.details.rates_excluded': 'Rates over {amount} excluded',
  'summary.details.no_minimum': 'No minimum purchase requirement',
  'summary.details.minimum_amount': 'Minimum purchase of {amount}',
  'summary.details.minimum_quantity': 'Minimum of {quantity} items',
  'summary.details.uses_per_order': 'Up to {number} uses per order',
  'summary.details.total_uses': '{number} total uses',
  'summary.details.once_per_customer': 'one use per customer',
  'summary.details.limited_to': 'Limited to {limits}',
  'summary.details.no_limits': 'No usage limits',
  'summary.details.combines_one': 'Combines with {first}',
  'summary.details.combines_two': 'Combines with {first} and {second}',
  'summary.details.combines_none': "Can't combine with other discounts",
  'summary.details.class.product': 'product discounts',
  'summary.details.class.order': 'order discounts',
  'summary.details.class.shipping': 'shipping discounts',
  'summary.details.today': 'today',
  'summary.details.active_from': 'Active from {from}',
  'summary.details.active_from_until': 'Active from {from} until {until}',
};

export default discounts;
